import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trophy, 
  Target, 
  TrendingUp, 
  Calendar, 
  Award, 
  Star, 
  CheckCircle, 
  Clock, 
  Users, 
  BarChart3,
  Zap,
  Heart,
  BookOpen,
  DollarSign,
  Home,
  GraduationCap,
  Car,
  CreditCard,
  FileText,
  Shield,
  Globe
} from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { useAuth } from '../contexts/AuthContext';
import { AnimatedCounter } from '../components/ui';

interface Milestone {
  id: string;
  title: string;
  description: string;
  category: string;
  icon: React.ElementType;
  progress: number;
  total: number;
  completed: boolean;
  unlocked: boolean;
  points: number;
  difficulty: 'easy' | 'medium' | 'hard';
  estimatedTime: string;
  nextSteps: string[];
}

interface ProgressStats {
  totalMilestones: number;
  completedMilestones: number;
  totalPoints: number;
  currentStreak: number;
  bestStreak: number;
  averageCompletionTime: number;
  categoryProgress: {
    [key: string]: {
      completed: number;
      total: number;
      percentage: number;
    };
  };
}

const MilestonesPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [stats, setStats] = useState<ProgressStats | null>(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState<'grid' | 'timeline'>('grid');

  useSEO({
    title: 'Milestones & Progress - MyNewStart',
    description: 'Visual journey map showing where you are, what\'s done, and what\'s next.',
    keywords: ['milestones', 'progress', 'achievements', 'goals', 'tracking'],
    breadcrumbs: [
      { name: 'Home', url: '/' },
      { name: 'Dashboard', url: '/dashboard' },
      { name: 'Milestones & Progress', url: '/milestones' }
    ]
  });

  useEffect(() => {
    loadMilestones();
    loadProgressStats();
  }, []);

  const loadMilestones = () => {
    // Mock data - in a real app, this would come from your API
    const mockMilestones: Milestone[] = [
      {
        id: '1',
        title: 'First Bank Account',
        description: 'Open your first U.S. bank account and understand basic banking',
        category: 'banking',
        icon: CreditCard,
        progress: 100,
        total: 100,
        completed: true,
        unlocked: true,
        points: 50,
        difficulty: 'easy',
        estimatedTime: '2-3 days',
        nextSteps: ['Set up online banking', 'Get a debit card', 'Learn about fees']
      },
      {
        id: '2',
        title: 'Credit Score Understanding',
        description: 'Learn about credit scores and how to build credit history',
        category: 'banking',
        icon: TrendingUp,
        progress: 75,
        total: 100,
        completed: false,
        unlocked: true,
        points: 75,
        difficulty: 'medium',
        estimatedTime: '1-2 weeks',
        nextSteps: ['Check your credit report', 'Apply for a secured credit card', 'Set up payment reminders']
      },
      {
        id: '3',
        title: 'Tax Filing Basics',
        description: 'Understand U.S. tax system and file your first tax return',
        category: 'taxes',
        icon: FileText,
        progress: 0,
        total: 100,
        completed: false,
        unlocked: true,
        points: 100,
        difficulty: 'hard',
        estimatedTime: '2-4 weeks',
        nextSteps: ['Gather tax documents', 'Choose filing method', 'Understand deductions']
      },
      {
        id: '4',
        title: 'Find Housing',
        description: 'Secure stable housing and understand rental agreements',
        category: 'housing',
        icon: Home,
        progress: 25,
        total: 100,
        completed: false,
        unlocked: true,
        points: 150,
        difficulty: 'hard',
        estimatedTime: '1-3 months',
        nextSteps: ['Research neighborhoods', 'Prepare rental application', 'Understand lease terms']
      },
      {
        id: '5',
        title: 'Job Search Success',
        description: 'Land your first job or advance in your career',
        category: 'career',
        icon: GraduationCap,
        progress: 0,
        total: 100,
        completed: false,
        unlocked: false,
        points: 200,
        difficulty: 'hard',
        estimatedTime: '2-6 months',
        nextSteps: ['Update resume', 'Network with professionals', 'Prepare for interviews']
      },
      {
        id: '6',
        title: 'Insurance Coverage',
        description: 'Get essential insurance coverage for health, auto, and life',
        category: 'insurance',
        icon: Shield,
        progress: 0,
        total: 100,
        completed: false,
        unlocked: false,
        points: 125,
        difficulty: 'medium',
        estimatedTime: '1-2 weeks',
        nextSteps: ['Research insurance options', 'Compare quotes', 'Understand coverage terms']
      }
    ];

    setMilestones(mockMilestones);
  };

  const loadProgressStats = () => {
    // Mock stats - in a real app, this would come from your API
    const mockStats: ProgressStats = {
      totalMilestones: 24,
      completedMilestones: 3,
      totalPoints: 175,
      currentStreak: 7,
      bestStreak: 15,
      averageCompletionTime: 12,
      categoryProgress: {
        banking: { completed: 1, total: 6, percentage: 17 },
        taxes: { completed: 0, total: 4, percentage: 0 },
        housing: { completed: 0, total: 5, percentage: 0 },
        career: { completed: 0, total: 4, percentage: 0 },
        insurance: { completed: 0, total: 3, percentage: 0 },
        education: { completed: 2, total: 2, percentage: 100 }
      }
    };

    setStats(mockStats);
  };

  const categories = [
    { id: 'all', name: 'All Categories', icon: Globe, color: 'bg-gray-500' },
    { id: 'banking', name: 'Banking & Credit', icon: CreditCard, color: 'bg-blue-500' },
    { id: 'taxes', name: 'Taxes & Finance', icon: FileText, color: 'bg-green-500' },
    { id: 'housing', name: 'Housing & Life', icon: Home, color: 'bg-purple-500' },
    { id: 'career', name: 'Career & Education', icon: GraduationCap, color: 'bg-orange-500' },
    { id: 'insurance', name: 'Insurance', icon: Shield, color: 'bg-red-500' }
  ];

  const filteredMilestones = selectedCategory === 'all' 
    ? milestones 
    : milestones.filter(m => m.category === selectedCategory);

  const getDifficultyColor = (difficulty: string) => {
    switch (difficulty) {
      case 'easy': return 'text-green-600 bg-green-100';
      case 'medium': return 'text-yellow-600 bg-yellow-100';
      case 'hard': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getProgressColor = (progress: number) => {
    if (progress === 100) return 'bg-green-500';
    if (progress >= 75) return 'bg-blue-500';
    if (progress >= 50) return 'bg-yellow-500';
    if (progress >= 25) return 'bg-orange-500';
    return 'bg-gray-300';
  };

  if (!stats) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/3 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-4">
          <Trophy className="w-10 h-10 inline-block mr-3 text-primary-600" />
          Milestones & Progress
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Visual journey map showing where you are, what's done, and what's next in your American journey.
        </p>
      </div>

      {/* Progress Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-primary-100 rounded-lg">
              <Trophy className="w-6 h-6 text-primary-600" />
            </div>
            <span className="text-2xl font-bold text-gray-900">
              <AnimatedCounter value={stats.completedMilestones} />/{stats.totalMilestones}
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Milestones Completed</h3>
          <p className="text-sm text-gray-600">Keep up the great progress!</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <Star className="w-6 h-6 text-yellow-600" />
            </div>
            <span className="text-2xl font-bold text-gray-900">
              <AnimatedCounter value={stats.totalPoints} />
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Total Points</h3>
          <p className="text-sm text-gray-600">Earned through achievements</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-green-100 rounded-lg">
              <Zap className="w-6 h-6 text-green-600" />
            </div>
            <span className="text-2xl font-bold text-gray-900">
              <AnimatedCounter value={stats.currentStreak} />
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Current Streak</h3>
          <p className="text-sm text-gray-600">Best: {stats.bestStreak} days</p>
        </div>

        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Clock className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-2xl font-bold text-gray-900">
              <AnimatedCounter value={stats.averageCompletionTime} />d
            </span>
          </div>
          <h3 className="font-semibold text-gray-900 mb-1">Avg. Completion</h3>
          <p className="text-sm text-gray-600">Time per milestone</p>
        </div>
      </div>

      {/* Category Progress */}
      <div className="bg-white rounded-xl shadow-md border border-gray-200 p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-6">Progress by Category</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(stats.categoryProgress).map(([category, progress]) => {
            const categoryInfo = categories.find(c => c.id === category);
            if (!categoryInfo) return null;

            return (
              <div key={category} className="p-4 border border-gray-200 rounded-lg">
                <div className="flex items-center mb-3">
                  <div className={`w-8 h-8 rounded-lg ${categoryInfo.color} flex items-center justify-center mr-3`}>
                    <categoryInfo.icon className="w-4 h-4 text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{categoryInfo.name}</h3>
                    <p className="text-sm text-gray-600">{progress.completed}/{progress.total} completed</p>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-500 h-2 rounded-full transition-all duration-500"
                    style={{ width: `${progress.percentage}%` }}
                  ></div>
                </div>
                <p className="text-sm text-gray-600 mt-2">{progress.percentage}% complete</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* View Mode Toggle */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Your Milestones</h2>
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'grid' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Grid View
          </button>
          <button
            onClick={() => setViewMode('timeline')}
            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              viewMode === 'timeline' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Timeline
          </button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map((category) => (
          <button
            key={category.id}
            onClick={() => setSelectedCategory(category.id)}
            className={`flex items-center px-4 py-2 rounded-full border transition-all duration-200 ${
              selectedCategory === category.id
                ? 'bg-primary-100 border-primary-500 text-primary-700'
                : 'bg-white border-gray-300 text-gray-600 hover:border-primary-300 hover:text-primary-600'
            }`}
          >
            <category.icon className="w-4 h-4 mr-2" />
            {category.name}
          </button>
        ))}
      </div>

      {/* Milestones Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredMilestones.map((milestone) => (
          <div
            key={milestone.id}
            className={`bg-white rounded-xl shadow-md border border-gray-200 p-6 transition-all duration-300 ${
              milestone.completed ? 'ring-2 ring-green-200 bg-green-50' : 
              milestone.unlocked ? 'hover:shadow-lg hover:border-primary-300' : 'opacity-60'
            }`}
          >
            {/* Header */}
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center mr-4 ${
                  milestone.completed ? 'bg-green-500' : 
                  milestone.unlocked ? 'bg-primary-500' : 'bg-gray-400'
                }`}>
                  <milestone.icon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 mb-1">{milestone.title}</h3>
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getDifficultyColor(milestone.difficulty)}`}>
                    {milestone.difficulty}
                  </span>
                </div>
              </div>
              {milestone.completed && (
                <CheckCircle className="w-6 h-6 text-green-500" />
              )}
            </div>

            {/* Description */}
            <p className="text-gray-600 mb-4">{milestone.description}</p>

            {/* Progress */}
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Progress</span>
                <span className="text-sm text-gray-600">{milestone.progress}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className={`h-2 rounded-full transition-all duration-500 ${getProgressColor(milestone.progress)}`}
                  style={{ width: `${milestone.progress}%` }}
                ></div>
              </div>
            </div>

            {/* Stats */}
            <div className="flex items-center justify-between text-sm text-gray-600 mb-4">
              <div className="flex items-center">
                <Star className="w-4 h-4 mr-1" />
                {milestone.points} points
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 mr-1" />
                {milestone.estimatedTime}
              </div>
            </div>

            {/* Next Steps */}
            {milestone.nextSteps.length > 0 && !milestone.completed && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">Next Steps:</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  {milestone.nextSteps.slice(0, 2).map((step, index) => (
                    <li key={index} className="flex items-center">
                      <div className="w-1.5 h-1.5 bg-primary-500 rounded-full mr-2"></div>
                      {step}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Action Button */}
            <button
              onClick={() => {
                if (milestone.unlocked) {
                  navigate(`/milestone/${milestone.id}`);
                }
              }}
              disabled={!milestone.unlocked}
              className={`w-full py-2 px-4 rounded-lg font-medium transition-colors ${
                milestone.completed
                  ? 'bg-green-100 text-green-700 cursor-default'
                  : milestone.unlocked
                  ? 'bg-primary-600 text-white hover:bg-primary-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {milestone.completed ? 'Completed' : milestone.unlocked ? 'Continue' : 'Locked'}
            </button>
          </div>
        ))}
      </div>

      {/* Call to Action */}
      <div className="mt-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl p-8 text-center text-white">
        <h2 className="text-2xl font-bold mb-4">Keep Building Your American Dream</h2>
        <p className="text-lg mb-6 opacity-90">
          Every milestone brings you closer to your goals. Stay motivated and track your progress!
        </p>
        <button
          onClick={() => navigate('/next-steps')}
          className="btn bg-white text-primary-600 hover:bg-gray-100"
        >
          View Next Steps
        </button>
      </div>
    </div>
  );
};

export default MilestonesPage;
